/**
 * Resolved camera storage keys – the DECRYPTED `master_key` secret per device and
 * era, held only in memory for the lifetime of the session.
 *
 * @remarks
 * This store is intentionally NOT persisted: clearing it on reload is the point.
 * Key **rotation** means a device can have several historical keys (eras), so this
 * store is a **per-era** cache keyed by the era's `createdAt`
 * (`{ [deviceId]: { [era]: masterKey } }`). The correct era is selected on demand
 * by recording date in `resolveMasterKey`, which decrypts the era's backup
 * (cheap AES-GCM) and refreshes the matching era slot. The plaintext `master_key`
 * is re-derived on demand from the encrypted backup in {@link camera_storage_keys}
 * plus the era's preserved password key in {@link camera_storage_passphrases}.
 * Consumers such as `HLSPlayer` / `CameraThumbnail` subscribe to it so playback
 * re-initialises the moment a key is resolved.
 */

import { get, writable } from 'svelte/store';

/** Map of `deviceId` -> (`era` createdAt -> base64 plaintext `master_key`). */
type ResolvedMasterKeyMap = Record<string, Record<string, string>>;

function create_camera_storage_resolved_keys() {
	const { subscribe, set, update } = writable<ResolvedMasterKeyMap>({});

	return {
		subscribe,
		set,
		setKey: (deviceId: string, era: string, key: string) => {
			if (!deviceId || !key) return;
			const slot = era ?? '';
			// Skip when unchanged: a writable always notifies on object writes, and
			// consumers that re-resolve on this store would otherwise loop.
			if (get(camera_storage_resolved_keys)[deviceId]?.[slot] === key) return;
			update((state) => ({
				...state,
				[deviceId]: { ...(state[deviceId] ?? {}), [slot]: key },
			}));
		},
		getKey: (deviceId: string, era: string): string | null => {
			if (!deviceId) return null;
			return get(camera_storage_resolved_keys)[deviceId]?.[era ?? ''] ?? null;
		},
		/** Removes a device's era (or the whole device when `era` is omitted). */
		remove: (deviceId: string, era?: string) =>
			update((state) => {
				if (!state[deviceId]) return state;
				if (era == null) {
					delete state[deviceId];
					return state;
				}
				const device = { ...state[deviceId] };
				delete device[era];
				return { ...state, [deviceId]: device };
			}),
		clear: () => set({}),
	};
}

export const camera_storage_resolved_keys = create_camera_storage_resolved_keys();
